import { useMemo } from "react";
import { useQuery } from "react-query";
import { getFloorAvailability } from "../../../utils/axios-requests";
import { useRemoteFetchFloorPlan } from "../../../hooks/Remote/FloorPlan/useRemoteFetchFloorPlan";
import type { FloorAvailability } from "../../FloorManager/typings/floor-inventory.entity";
import type { TeamMember } from "../../Booking-Form/typings/team-member";
import {
  buildColleagueFloorAvailabilityArgs,
  colleagueFloorPlanIsoRange,
  pickDefaultColleagueFloorId,
  toColleagueFloorChips
} from "./colleague-workplace-floor.functions";

type Args = {
  enabled: boolean;
  locationInventoryId?: number;
  date: string;
  startTime: string;
  endTime: string;
  user: Pick<TeamMember, "userId" | "firstName" | "surname">;
  selectedFloorInventoryId?: number;
  highlightInventoryId?: number;
};

export function useColleagueWorkplaceFloorData({
  enabled,
  locationInventoryId,
  date,
  startTime,
  endTime,
  user,
  selectedFloorInventoryId,
  highlightInventoryId
}: Args) {
  const availabilityArgs = useMemo(() => {
    if (locationInventoryId == null || locationInventoryId <= 0 || date === "") {
      return null;
    }

    return buildColleagueFloorAvailabilityArgs({
      locationInventoryId,
      user,
      date,
      startTime,
      endTime
    });
  }, [date, endTime, locationInventoryId, startTime, user]);

  const availabilityQuery = useQuery(
    ["colleagueWorkplaceFloorAvailability", availabilityArgs],
    async () => {
      if (!availabilityArgs) {
        return [];
      }
      const { data } = await getFloorAvailability(availabilityArgs);
      return data as FloorAvailability[];
    },
    { enabled: enabled && availabilityArgs != null }
  );

  const floorInventory = availabilityQuery.data ?? [];
  const floorChips = useMemo(() => toColleagueFloorChips(floorInventory), [floorInventory]);

  const resolvedFloorId = useMemo(() => {
    if (selectedFloorInventoryId != null && selectedFloorInventoryId > 0) {
      return selectedFloorInventoryId;
    }
    return pickDefaultColleagueFloorId(floorInventory, highlightInventoryId);
  }, [floorInventory, highlightInventoryId, selectedFloorInventoryId]);

  const currentFloor = useMemo(() => {
    if (!floorInventory.length) {
      return undefined;
    }
    return floorInventory.find(floor => floor.id === resolvedFloorId) ?? floorInventory[0];
  }, [floorInventory, resolvedFloorId]);

  const planRange = useMemo(
    () => colleagueFloorPlanIsoRange(date, startTime, endTime),
    [date, endTime, startTime]
  );

  const floorPlanId = currentFloor?.id ?? 0;
  const floorPlanQuery = useRemoteFetchFloorPlan(
    floorPlanId,
    planRange?.start ?? "",
    planRange?.end ?? null,
    enabled && floorPlanId > 0 && planRange != null
  );

  return {
    floorInventory,
    floorChips,
    currentFloor,
    resolvedFloorId: currentFloor?.id,
    floorPlan: floorPlanQuery.data,
    isLoading: availabilityQuery.isLoading || floorPlanQuery.isLoading,
    isError: availabilityQuery.isError || floorPlanQuery.isError,
    refetch: () => {
      availabilityQuery.refetch();
      floorPlanQuery.refetch();
    }
  };
}
